import { Standing } from "../models/Standing";
import { TeamPrediction } from "../models/TeamPrediction";

export interface PredictionScore {
  /** Mean distance between predicted and current rank over the teams found in the standings. */
  score: number;
  /** Teams sitting on exactly the predicted rank. */
  exact: number;
}

/** A division is only scored once somebody in it has played: before that the ranks are just list order. */
export const hasGamesPlayed = (standings: Standing[] | undefined): boolean =>
  !!standings && standings.some((s) => s.gamesPlayed > 0);

/**
 * One division prediction against the current standings - the same formula as the backend
 * leaderboard (StandingsPredictionController.GetPredictionLeaderboard), so the numbers the predict
 * pages show agree with it. Null when the division isn't scored yet or no team matches.
 */
export function scorePrediction(teams: TeamPrediction[], standings: Standing[] | undefined): PredictionScore | null {
  if (!standings || !hasGamesPlayed(standings) || teams.length === 0) return null;

  const rankByTeam = new Map<string, number>();
  for (const s of standings) {
    if (s.team && !rankByTeam.has(s.team.teamId)) rankByTeam.set(s.team.teamId, s.rank);
  }

  let difference = 0;
  let matched = 0;
  let exact = 0;
  for (const t of teams) {
    const rank = rankByTeam.get(t.teamId);
    if (rank === undefined) continue;
    difference += Math.abs(t.predictedRank - rank);
    matched++;
    if (rank === t.predictedRank) exact++;
  }
  return matched > 0 ? { score: difference / matched, exact } : null;
}
